import { Grid, IconButton, Typography, Box, Tooltip } from '@mui/material';
import { IReservation } from '../../../interfaces/IPartnerPms';
import { RoomStatusData, RoomStatusEnum } from '../../../data/Enums';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import { useEffect, useMemo, useState } from 'react';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import DefaultConstants from '../../../data/Constants';
import { useUserContext } from '../../../contexts/UserContext';
import { Edit } from '@mui/icons-material';
import useDialog from '../../../hooks/useDialog';
import { ActionButton } from '../../Button';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import UpdateObservationsDialog from './UpdateObservationsDialog';
import FormHelper from '../../../helpers/FormHelper';
import PartnerPmsService from '../../../services/PartnerPmsService';


const ownerReservation = RoomStatusData[RoomStatusEnum.OwnerReservation];
const vipReservation = RoomStatusData[RoomStatusEnum.VipReservation];
const expectedCheckIn = RoomStatusData[RoomStatusEnum.ExpectedCheckIn];
const expectedCheckOut = RoomStatusData[RoomStatusEnum.ExpectedCheckOut];


interface IReservationInformationsDialogProps extends IDefaultDialogProps {
    reservation?: IReservation;
    isCheckIn?: boolean;
    handleRefreshSelectedRoom: () => void;
}


const ReservationInformationsDialog: React.FC<IReservationInformationsDialogProps> = ({
    open,
    handleClose,
    reservation = undefined,
    isCheckIn = false,
    handleRefreshSelectedRoom
}) => {
    const { translate } = useLanguageContext();
    const { userPermissions } = useUserContext();

    const [expandedObservations, setExpandedObservations] = useState<boolean>(false);

    const updateObservationsDialog = useDialog();


    useEffect(() => {
        if (reservation) {
            setExpandedObservations(false);
            updateObservationsDialog.handleClose();
        }
        // eslint-disable-next-line
    }, [reservation]);

    
    const reservationExpectedCheckIn = useMemo(() => {
        return !isCheckIn && DateTimeHelper.Equals(reservation?.checkInDate ?? '', DateTimeHelper.Now());
    }, [reservation, isCheckIn]);

    const reservationExpectedCheckOut = useMemo(() => {
        return isCheckIn && DateTimeHelper.Equals(reservation?.checkOutDate ?? '', DateTimeHelper.Now());
    }, [reservation, isCheckIn]);


    const observations = useMemo(() => {
        let observations = reservation?.observations;

        if (!observations || observations.trim().length === 0) {
            observations = '-';
        }
        
        if (observations.length <= 100) {
            return <Typography variant='body2'>
                {observations}
            </Typography>;
        }

        return <Typography variant='body2' className='cursor-pointer' onClick={() => setExpandedObservations(!expandedObservations)}>
            {expandedObservations ? observations : `${observations.slice(0, 100)}...`}

            <Typography
                component='p'
                variant='caption'
                className='texttransform-uppercase color-primary'
                marginTop={0.5}
            >
                {translate(expandedObservations ? 'seeLess' : 'seeMore')}
            </Typography>
        </Typography>;
        
        // eslint-disable-next-line
    }, [reservation, expandedObservations]);


    const editReservationObservations = useMemo(() => {
        return userPermissions.includes(`Governance.RoomOverview.EditReservationObservations`);
    }, [userPermissions]);


    const headerActionsLeft = useMemo(() => {
        if (reservation?.ownerGuestType) {
            return <Box>
                <Tooltip title={translate(ownerReservation.name)} placement="bottom-start" arrow disableInteractive>
                    <IconButton size='small'>
                        <ownerReservation.Icon fontSize='inherit' htmlColor={ownerReservation.color} />
                    </IconButton>
                </Tooltip>
            </Box>;
        }
        
        else if (reservation?.vipGuestType) {
            return <Box>
                <Tooltip title={translate(vipReservation.name)} placement="bottom-start" arrow disableInteractive>
                    <IconButton size='small'>
                        <vipReservation.Icon fontSize='inherit' htmlColor={vipReservation.color} />
                    </IconButton>
                </Tooltip>
            </Box>;
        }
        
        return null;
        // eslint-disable-next-line
    }, [reservation, translate]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />
        ];
        // eslint-disable-next-line
    }, [translate]);

    
    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={`${translate('reservation')} ${reservation?.id ?? '...'}`}
                headerActionsLeft={headerActionsLeft}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                    <Grid item md={4} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('checkIn')}
                        </Typography>

                        <Typography
                            variant='body2'
                            className='display-flex alignitems-center' 
                            sx={{ color: reservationExpectedCheckIn ? expectedCheckIn.color : 'initial' }}
                        >
                            {
                                reservationExpectedCheckIn && <expectedCheckIn.Icon className='inline-icon' />
                            }

                            {DateTimeHelper.Format(reservation?.checkInDate ?? '-')}
                        </Typography>
                    </Grid>

                    <Grid item md={4} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('checkOut')}
                        </Typography>

                        <Typography
                            variant='body2'
                            className='display-flex alignitems-center' 
                            sx={{ color: reservationExpectedCheckOut ? expectedCheckOut.color : 'initial' }}
                        >
                            {
                                reservationExpectedCheckOut && <expectedCheckOut.Icon className='inline-icon' />
                            }

                            {DateTimeHelper.Format(reservation?.checkOutDate ?? '-')}
                        </Typography>
                    </Grid>

                    <Grid item md={2} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('adults')}
                        </Typography>

                        <Typography variant='body2'>
                            {reservation?.numberAdults ?? 0}
                        </Typography>
                    </Grid>

                    <Grid item md={2} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('childrens')}
                        </Typography>

                        <Typography variant='body2'>
                            {(+(reservation?.numberChildren1 ?? 0)) + (+(reservation?.numberChildren2 ?? 0))}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('mainGuest')}
                        </Typography>

                        <Typography variant='body2'>
                            {reservation?.mainGuestName ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('guestType')}
                        </Typography>

                        <Typography variant='body2'>
                            {reservation?.guestType ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} className='display-flex justifycontent-spacebetween'>
                        <Box>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('observations')}
                            </Typography>

                            {observations}
                        </Box>

                        {
                            editReservationObservations && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateObservationsDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>
                </Grid>
            </Dialog>
            
            {
                reservation && editReservationObservations && <UpdateObservationsDialog
                    open={updateObservationsDialog.open}
                    handleClose={() => updateObservationsDialog.handleClose()}
                    observations={reservation.observations ?? ''}
                    handleSubmit={(event) => {
                        event.preventDefault();

                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'reservationId', reservation.id);

                        PartnerPmsService.UpdateReservationObservations(formData).then(() => {
                            handleRefreshSelectedRoom();
                            updateObservationsDialog.handleClose();
                        });
                    }}
                />
            }
        </>
    );
}

export default ReservationInformationsDialog;